import { Check, ChevronLeft, ChevronRight, Clock3, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dayLabel, fmtDay, metaFor, prettyMinutes, withClockTimes } from '@/lib/plannerUtils';

/** Today's run sheet: one timed block after another, exactly like a timetable. */
export function TodayTab({ plan, day, cursor, onCursor, onJump, onToggleDone }) {
  const navigate = useNavigate();
  const items = withClockTimes(day);
  const done = items.filter(i => i.done).length;

  return (
    <div data-testid="planner-today">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-none">
            {new Date(`${cursor}T00:00:00`).getDate()}
          </span>
          <span className="font-body text-[11px] uppercase tracking-widest text-slate-400 leading-tight">
            {fmtDay(cursor, { weekday: 'long' })}<br />{fmtDay(cursor, { month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-heading uppercase tracking-widest text-indigo-500">
            {cursor === plan?.today ? dayLabel(day) : `${dayLabel(day)} · next study day`}
          </p>
          <p className="font-heading text-base md:text-lg font-bold text-slate-900">
            {day ? prettyMinutes(day.minutes) : 'Free day'}
          </p>
        </div>
        <span className="font-heading text-sm text-slate-500" data-testid="planner-today-progress">
          {done}/{items.length || 0}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 mt-3">
        <button onClick={() => onCursor(-1)} data-testid="planner-prev-day"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
          <ChevronLeft className="w-3 h-3" /> Previous day
        </button>
        <div className="flex items-center gap-1.5">
          <button onClick={() => onCursor(0)} data-testid="planner-back-to-today"
            className="px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
            Back to today
          </button>
          <input type="date" value={cursor} onChange={e => e.target.value && onJump?.(e.target.value)}
            data-testid="planner-jump-date" title="Jump to any date"
            className="h-[30px] px-2 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 bg-white" />
        </div>
        <button onClick={() => onCursor(1)} data-testid="planner-next-day"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
          Next day <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {!items.length && (
        <p className="mt-6 text-center text-sm text-slate-500 font-body" data-testid="planner-today-empty">
          Nothing planned for this day — rest, or step forward to the next study day.
        </p>
      )}

      <div className="mt-3 space-y-2">
        {items.map(item => {
          const meta = metaFor(item);
          const Icon = meta.icon;
          return (
            <div key={item.ref} data-testid={`planner-item-${item.ref}`}
              className={`flex gap-3 rounded-2xl border bg-white p-3 transition-colors ${item.done ? 'opacity-60' : 'hover:border-indigo-200'}`}>
              <div className="w-16 flex-shrink-0 text-right">
                <p className="font-heading text-[12px] text-slate-700">{item.clock}</p>
                <p className="font-body text-[10px] text-slate-400">{prettyMinutes(item.minutes)}</p>
              </div>
              <div className="flex-1 min-w-0">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-heading uppercase tracking-wide ${meta.tone}`}>
                  <Icon className="w-3 h-3" /> {item.subject}
                </span>
                <p className={`font-heading text-sm md:text-base text-slate-900 mt-1 ${item.done ? 'line-through' : ''}`}>
                  {item.label}
                </p>
                {item.route && (
                  <button onClick={() => navigate(item.route)} data-testid={`planner-open-${item.ref}`}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-heading text-indigo-600 hover:underline">
                    Open it <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
              <button onClick={() => onToggleDone(item.ref, !item.done)} data-testid={`planner-done-${item.ref}`}
                aria-label={item.done ? 'Mark as not done' : 'Mark as done'}
                className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 self-center transition-colors ${item.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-400'}`}>
                {item.done && <Check className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
      </div>

      {day?.minutes > 0 && (
        <p className="mt-3 text-[11px] font-body text-slate-400 flex items-center gap-1" data-testid="planner-today-total">
          <Clock3 className="w-3 h-3" /> {prettyMinutes(day.minutes)} planned for this day
        </p>
      )}
    </div>
  );
}

export default TodayTab;
